import type { PrefillPostData } from '~publish/hooks/usePostComposer'
import type { RpcUpdateMedia } from '~publish/legacy/post/types'
import { isExtendedMedia } from '~publish/pages/Create/helpers'
import { MediaType } from '~publish/pages/Create/types'
import type { ExtendedIdea, Idea, Media } from '~publish/pages/Create/types'

export function getFirstVideo(media: Media[]): Media | undefined {
  if (!media) return
  return media.find((m) => m.type === MediaType.video)
}

export function getFirstDocument(media: Media[]): Media | undefined {
  if (!media) return
  return media.find((m) => m.type === MediaType.document)
}

const getPostTypeFromIdea = (idea: Idea): string => {
  let type = 'text'
  if (idea.content.media) {
    const mediaLength = idea.content.media.length
    type = mediaLength > 0 ? 'multipleImage' : 'image'

    const document = idea.content.media.find(
      (m) => m.type === MediaType.document,
    )
    if (document) {
      type = 'document'
    }
  }
  return type
}

function parseIdeaMedia(media: Media[] = []): RpcUpdateMedia[] {
  // only one video is allowed in the composer, no extra media here
  const video = getFirstVideo(media)
  if (video) {
    if (!isExtendedMedia(video)) {
      throw new Error(
        'Cannot create a post from a video with incomplete metadata',
      )
    }

    return [
      {
        video: {
          id: video.id ?? 'no-id?',
          title: '',
          thumbnails: [video.thumbnailUrl ?? ''],
          details: {
            height: video.height,
            width: video.width,
            duration: video.duration,
            duration_millis: video.durationMs,
            file_size: video.size,
            location: video.location ?? '',
            transcoded_location: video.transcodedLocation ?? '',
          },
        },
        thumbnail: video.thumbnailUrl ?? '',
        uploaded: 'true',
        progress: '100',
        alt_text: video.alt,
        source: video.url,
        uploading_type: 'video',
      },
    ]
  }

  const document = getFirstDocument(media)
  if (document) {
    return [
      {
        progress: '100',
        uploaded: 'true',
        thumbnail: document.thumbnailUrl ?? '',
        uploading_type: 'document',
        document: {
          id: document.id,
          title: '',
          details: {
            location: document.url,
            pageCount: 0,
            fileSizeBytes: 0,
          },
        },
      },
    ]
  }
  // if we don't have a video, map throug images and gifs
  return media
    .filter((m) => m.type !== MediaType.video)
    .map((m) => {
      return {
        alt_text: m.alt,
        progress: '100',
        source: m.source,
        uploaded: 'true',
        thumbnail: m.thumbnailUrl || '',
        photo: m.url,
        picture: m.url,
        width: isExtendedMedia(m) && m.width ? `${m.width}` : '',
        height: isExtendedMedia(m) && m.height ? `${m.height}` : '',
      }
    })
}

// Parse to accommodate text only ideas for now
export const parseIdeaToComposerPost = (
  idea: ExtendedIdea,
  user: { id: string; email: string },
): PrefillPostData => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [media, ...extra_media] = parseIdeaMedia(idea.content.media ?? [])
  const type = getPostTypeFromIdea(idea)
  const postText = idea.content.text ?? ''

  return {
    id: '',
    status: '',
    annotations: [],
    notes: [],
    createdAt: 0,
    day: '',
    dueAt: 0,
    due_at: 0,
    hasPushNotifications: false,
    headerDetails: {},
    imageUrls: [],
    isDraft: false,
    isFixed: false,
    isPastDue: false,
    isReminder: false,
    isPastReminder: false,
    isSent: false,
    linkAttachment: {},
    links: [],
    media,

    extra_media,
    needsApproval: false,
    pinned: false,
    postDetails: {
      postAction: '',
      isRetweet: false,
      isCustomScheduled: false,
      isInstagramReminder: false,
      isPendingTiktokPost: false,
    },
    profileId: '',
    profileService: '',
    profileServiceType: '',
    profileTimezone: '',
    profile_service: '',
    retweetCommentLinks: [],
    scheduledAt: 0,
    scheduled_at: 0,
    serviceLink: '',
    serviceUpdateId: '',
    source_url: '',
    statistics: {},
    subprofile_id: '',
    title: '',
    type,
    updateType: '',
    user: {
      id: user.id,
      avatar: '',
      email: user.email,
    },
    imageSrc: '',
    campaignDetails: undefined,
    text: postText,
    postContent: {
      text: postText,
      type,
    },
    ideaId: idea.id,
    fromIdea: true,
    thread: undefined,
    tags: idea.content?.tags?.map((tag) => tag.id) || [],
  }
}
